import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowPathIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import useWarehouseModel from './useWarehouseModel';
import WarehouseScene from './WarehouseScene';
import ViewSelector from './ViewSelector';
import RackDrawer from './RackDrawer';
import LocationDrawer from './LocationDrawer';
import OutsideStructureDrawer from './OutsideStructureDrawer';
import LocationMasterPanel from './LocationMasterPanel';
import ConversationPane from './ConversationPane';
import ReplenishmentPanel, { movesForRecommendation, useReplenishmentPlan } from './ReplenishmentPanel';
import CapacityPanel, { useLocationCapacity } from './CapacityPanel';
import useLocationCapacityAccess from './useLocationCapacityAccess';
import { formatDateTime } from './format';
import { ROUTES } from '../../consts/general';

export const buildSceneCommand = (command, id = Date.now()) => ({
  id,
  camera: command.camera,
  highlight: command.highlight,
});

export const mergeSavedLocation = (current, saved) => (
  saved?.location_code && saved.location_code === current?.location_code
    ? { ...current, ...saved }
    : current
);

const WarehousePage = () => {
  const { scene, model, loading, error, refresh } = useWarehouseModel();
  const [searchParams] = useSearchParams();
  const canSeeCapacity = useLocationCapacityAccess();
  const [overlay, setOverlay] = useState(() => {
    const view = searchParams.get('view');
    if (view === 'replenishment') return 'replenishment';
    if (view === 'capacity' && canSeeCapacity) return 'capacity';
    return 'standard';
  });
  const replenishment = useReplenishmentPlan({ enabled: overlay === 'replenishment' });
  const capacity = useLocationCapacity({ enabled: overlay === 'capacity' && canSeeCapacity });
  const [search, setSearch] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedCell, setSelectedCell] = useState([]);
  const [selectedRack, setSelectedRack] = useState(null);
  const [selectedOutsideRun, setSelectedOutsideRun] = useState(null);
  const [selectedBay, setSelectedBay] = useState(1);
  const [showUnplaced, setShowUnplaced] = useState(false);
  const [orbit, setOrbit] = useState(42);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [sceneCommand, setSceneCommand] = useState(null);
  const [master, setMaster] = useState(null);

  const provisionalCount = useMemo(() => [...model.byCode.values()].filter((location) => location.placement?.provisional).length, [model]);
  const outsideLocations = useMemo(() => [...(model.outsideStructure?.values() || [])].flat(), [model]);
  const noFixedSpotCount = model.noFixedSpot?.length || 0;
  const inFrameCount = model.byCode.size - model.unplaced.length - noFixedSpotCount - outsideLocations.length;
  const capacityProfileById = useMemo(() => new Map((scene?.capacityProfiles || []).map((profile) => [profile.profile_id, profile])), [scene]);
  const conversationFocus = useMemo(() => {
    if (selectedLocation?.location_code) {
      return {
        kind: 'location',
        label: selectedLocation.location_code,
        locationCodes: [selectedLocation.location_code],
      };
    }
    if (selectedCell.length) {
      const first = selectedCell[0];
      const run = model.runs.find((item) => item.id === first.placement?.runId);
      return {
        kind: 'bay',
        label: `${run?.label || first.placement?.runId || 'Warehouse'} · Bay ${selectedBay}`,
        locationCodes: selectedCell.map((location) => location.location_code).slice(0, 40),
      };
    }
    if (selectedRack?.label) {
      return {
        kind: 'rack',
        label: selectedRack.label,
        locationCodes: [...model.byCode.values()]
          .filter((location) => location.placement?.runId === selectedRack.id)
          .map((location) => location.location_code)
          .slice(0, 40),
      };
    }
    if (search && model.bySku.has(search)) {
      return {
        kind: 'sku',
        label: search,
        locationCodes: [...new Set((model.bySku.get(search) || []).map((row) => row.location_code))].slice(0, 40),
      };
    }
    return { kind: 'overview', label: 'Warehouse', locationCodes: [] };
  }, [model, search, selectedBay, selectedCell, selectedLocation, selectedRack]);
  const conversationSummary = useMemo(() => ({
    totalLocations: model.byCode.size,
    inFrame: inFrameCount,
    outsideStructure: outsideLocations.length,
    unplaced: model.unplaced.length,
    noFixedSpot: noFixedSpotCount,
    provisional: provisionalCount,
    shipheroLastSync: scene?.shipheroSync?.last_update || null,
    shipheroStatus: scene?.shipheroSync?.process_status || null,
  }), [inFrameCount, model, noFixedSpotCount, outsideLocations.length, provisionalCount, scene]);

  const selectLocation = (location) => {
    setShowUnplaced(false);
    setSelectedRack(null);
    setSelectedOutsideRun(null);
    setSelectedCell([]);
    setSelectedLocation(location);
  };
  const selectCell = (locations) => {
    setSelectedRack(null);
    setSelectedOutsideRun(null);
    setSelectedLocation(locations.length === 1 ? locations[0] : null);
    setSelectedCell(locations.length > 1 ? locations : []);
  };
  const selectRack = (run) => {
    setSelectedLocation(null);
    setSelectedCell([]);
    setSelectedRack(run);
    setSelectedOutsideRun(null);
    setSelectedBay(1);
  };
  const closeDrawers = () => {
    setSelectedLocation(null);
    setSelectedCell([]);
    setSelectedRack(null);
    setSelectedOutsideRun(null);
  };
  const selectOutsideRun = (runId) => {
    setSelectedLocation(null);
    setSelectedCell([]);
    setSelectedRack(null);
    setSelectedOutsideRun(model.runs.find((run) => run.id === runId) || null);
  };
  const focusSku = (sku) => {
    setSelectedLocation(null);
    setSelectedRack(null);
    setSearch(sku);
  };
  const issueSceneCommand = (command) => setSceneCommand(buildSceneCommand(command));
  const focusCapacityRack = (run) => {
    selectRack(run);
    issueSceneCommand({ camera: { runId: run.id, zoom: 1.75 } });
  };
  const selectMasterLocation = (code) => {
    const location = model.byCode.get(code);
    setMaster(null);
    if (location) selectLocation(location);
  };
  const focusRecommendation = (row) => {
    const location = model.byCode.get(row.location_code);
    if (location) selectLocation(location);
    const moves = movesForRecommendation(row);
    issueSceneCommand({
      camera: { locationCodes: [row.location_code, ...moves.map((move) => move.from)], zoom: 1.7, label: `${row.outcome} · ${row.location_code}` },
      highlight: { locationCodes: [row.location_code, ...moves.map((move) => move.from)], skus: [row.sku], moves },
    });
  };
  const deepLinkedLocation = searchParams.get('location');
  useEffect(() => {
    if (!deepLinkedLocation || !replenishment.plan || model.byCode.size === 0) return;
    const row = replenishment.plan.recommendations.find((item) => item.location_code === deepLinkedLocation);
    if (row) focusRecommendation(row);
    else {
      const location = model.byCode.get(deepLinkedLocation);
      if (location) selectLocation(location);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkedLocation, replenishment.plan, model.byCode.size]);

  if (loading && !scene) {
    return <div className="flex h-[calc(100vh-48px)] items-center justify-center text-sm text-gray-500">Loading warehouse scene…</div>;
  }
  if (error && !scene) {
    return <div className="flex h-[calc(100vh-48px)] flex-col items-center justify-center gap-3 text-sm text-gray-600"><p>{error}</p><button type="button" onClick={refresh} className="rounded-md bg-black px-3 py-2 text-xs font-medium text-white">Retry</button></div>;
  }

  return (
    <div className="flex h-[calc(100vh-48px)] min-h-[680px] flex-col overflow-hidden bg-white">
      <header className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-gray-200 bg-white px-4 py-2.5">
        <div className="min-w-0">
          <div className="flex items-center gap-2"><h1 className="truncate text-base font-semibold text-gray-900">Location Master</h1><span className="rounded bg-accent/20 px-2 py-0.5 text-[10px] font-medium text-gray-800">Digital twin</span></div>
          <p className="mt-0.5 text-xs text-gray-500">{inFrameCount} in frame · {outsideLocations.length} outside structure · {model.unplaced.length} unplaced · {noFixedSpotCount} no fixed spot · {provisionalCount} provisional{scene?.geometry ? ` · ${scene.geometry.validated} validated · ${Object.values(scene.geometry.byReview || {}).reduce((sum, count) => sum + count, 0)} flagged` : ''} · ShipHero last sync {formatDateTime(scene?.shipheroSync?.last_update)}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative hidden min-w-[230px] sm:block"><MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-2 h-4 w-4 text-gray-400" /><input aria-label="Search warehouse" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search SKU, product, location, or PO..." className="h-8 w-full rounded-md border border-gray-300 pl-8 pr-7 text-xs outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900" />{search && <button type="button" onClick={() => setSearch('')} className="absolute right-1.5 top-1.5 rounded p-0.5 text-gray-400 hover:text-gray-800"><XMarkIcon className="h-4 w-4" /></button>}</div>
          <ViewSelector value={overlay} onChange={setOverlay} hidden={canSeeCapacity ? [] : ['capacity']} />
          <Link to={ROUTES.REPORTS_WAREHOUSE_RECONCILIATION} className="text-xs text-blue-700 hover:underline">Reconciliation report →</Link>
          <button type="button" onClick={() => setMaster({ tab: 'locations' })} className="inline-flex h-8 items-center rounded-md border border-gray-200 bg-white px-2.5 text-xs font-medium text-gray-600 shadow-sm hover:bg-gray-50">Master table</button>
          <button type="button" onClick={() => { refresh(); if (overlay === 'capacity' && canSeeCapacity) capacity.refresh(); }} className="inline-flex h-8 items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 text-xs font-medium text-gray-600 shadow-sm hover:bg-gray-50"><ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />Refresh</button>
        </div>
      </header>
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        <section className="relative min-h-0 flex-1 overflow-hidden border-b border-gray-200 lg:border-b-0 lg:border-r">
          <WarehouseScene
            model={model}
            outsideLocations={outsideLocations}
            overlay={overlay}
            replenishment={replenishment.outcomeByLocation}
            search={search}
            orbit={orbit}
            zoom={zoom}
            pan={pan}
            sceneCommand={sceneCommand}
            highlight={sceneCommand?.highlight}
            unplacedOpen={showUnplaced}
            onUnplacedOpenChange={setShowUnplaced}
            onOrbit={(delta) => setOrbit((value) => (value + delta + 360) % 360)}
            onZoom={(delta) => setZoom((value) => Math.max(0.55, Math.min(3, value + delta)))}
            onPan={setPan}
            onSelectLocation={selectLocation}
            onSelectCell={selectCell}
            onSelectRack={selectRack}
            onSelectBay={setSelectedBay}
            onShowOutside={selectOutsideRun}
            onShowUnplaced={() => setShowUnplaced(true)}
            onSearchSku={setSearch}
          />
          {overlay === 'replenishment' && <ReplenishmentPanel plan={replenishment.plan} loading={replenishment.loading} error={replenishment.error} activeLocation={selectedLocation?.location_code} onRefresh={replenishment.refresh} onFocus={focusRecommendation} onClose={() => setOverlay('standard')} />}
          {overlay === 'capacity' && canSeeCapacity && <CapacityPanel data={capacity.data} loading={capacity.loading} error={capacity.error} runs={model.runs} onSelectRack={focusCapacityRack} onRefresh={capacity.refresh} onClose={() => setOverlay('standard')} />}
          {search && <div className="absolute left-3 top-20 z-30 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-600 shadow-sm sm:hidden">Search: {search}</div>}
          {(selectedLocation || selectedCell.length > 0) && <LocationDrawer location={selectedLocation} locations={selectedCell} capacityProfile={capacityProfileById.get(selectedLocation?.capacity_profile_id)} options={{ locationGroups: scene?.locationGroups || [], capacityProfiles: scene?.capacityProfiles || [] }} onSaved={(data) => {
            const saved = data;
            if (saved?.location_code && saved.location_code === selectedLocation?.location_code) {
              setSelectedLocation((current) => mergeSavedLocation(current, saved));
            }
            return refresh();
          }} onOpenMaster={setMaster} onSelectLocation={selectLocation} onSearchSku={focusSku} onClose={closeDrawers} />}
          {!selectedLocation && !selectedCell.length && selectedRack && <RackDrawer run={selectedRack} model={model} onOpenMaster={setMaster} outsideLocations={model.outsideStructure?.get(selectedRack.id) || []} selectedBay={selectedBay} onSelectBay={setSelectedBay} onSelectLocation={selectLocation} onClose={closeDrawers} />}
          {!selectedLocation && !selectedCell.length && !selectedRack && selectedOutsideRun && <OutsideStructureDrawer run={selectedOutsideRun} locations={model.outsideStructure?.get(selectedOutsideRun.id) || []} onSelectLocation={selectLocation} onClose={closeDrawers} />}
        </section>
        <ConversationPane model={model} focus={conversationFocus} summary={conversationSummary} onFocusLocation={selectLocation} onOverlay={setOverlay} onShowUnplaced={() => setShowUnplaced(true)} onSceneCommand={issueSceneCommand} onSearch={setSearch} onOpenMaster={setMaster} onRefresh={refresh} />
      </div>
      {master && <LocationMasterPanel initialTab={master.tab} initialArea={master.area} initialQuery={master.q} onClose={() => setMaster(null)} onSelectLocation={selectMasterLocation} onSaved={refresh} />}
    </div>
  );
};

export default WarehousePage;
